/**
 * Strategic Call-to-Action Component
 *
 * Quick Win for FRONTEND-006: AIALGO Frontend Metrics Optimization
 * Priority: High | Estimated Effort: 2h
 *
 * Reusable CTAs for strategic placement throughout content to improve conversion rates
 */

import { ReactNode } from 'react'
import { Button } from './button'
import { cn } from '@/lib/utils'
import {
  ArrowRight,
  BookOpen,
  MessageSquare,
  Calendar,
  Download,
  PlayCircle,
  CheckCircle2,
} from 'lucide-react'

export interface StrategicCTAProps {
  /**
   * Visual variant of the CTA
   */
  variant?: 'default' | 'newsletter' | 'consultation' | 'download' | 'demo' | 'community'
  /**
   * Size of the CTA
   */
  size?: 'compact' | 'default' | 'prominent'
  /**
   * CTA title
   */
  title: string
  /**
   * CTA description
   */
  description?: string
  /**
   * Primary button text
   */
  primaryButtonText: string
  /**
   * Primary button action
   */
  onPrimaryClick: () => void
  /**
   * Secondary button text (optional)
   */
  secondaryButtonText?: string
  /**
   * Secondary button action (optional)
   */
  onSecondaryClick?: () => void
  /**
   * Features/benefits list (optional)
   */
  features?: string[]
  /**
   * Social proof text (optional)
   */
  socialProof?: string
  /**
   * Custom icon for the primary button
   */
  icon?: ReactNode
  /**
   * Additional CSS classes
   */
  className?: string
}

export function StrategicCTA({
  variant = 'default',
  size = 'default',
  title,
  description,
  primaryButtonText,
  onPrimaryClick,
  secondaryButtonText,
  onSecondaryClick,
  features,
  socialProof,
  icon,
  className,
}: StrategicCTAProps) {
  const variantStyles = {
    default: 'bg-card border-border',
    newsletter: 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800',
    consultation: 'bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200 dark:border-purple-800',
    download: 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800',
    demo: 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-orange-200 dark:border-orange-800',
    community: 'bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20 border-cyan-200 dark:border-cyan-800',
  }

  const sizeStyles = {
    compact: 'p-4',
    default: 'p-6',
    prominent: 'p-8',
  }

  const defaultIcon = {
    default: <ArrowRight className="h-4 w-4" />,
    newsletter: <BookOpen className="h-4 w-4" />,
    consultation: <Calendar className="h-4 w-4" />,
    download: <Download className="h-4 w-4" />,
    demo: <PlayCircle className="h-4 w-4" />,
    community: <MessageSquare className="h-4 w-4" />,
  }

  return (
    <div
      className={cn(
        'rounded-lg border-2 shadow-sm',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>

        {/* Features List */}
        {features && features.length > 0 && (
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{feature}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            className={size === 'prominent' ? 'text-base px-6 py-3' : ''}
            onClick={onPrimaryClick}
          >
            {primaryButtonText}
            {icon || defaultIcon[variant]}
          </Button>
          {secondaryButtonText && onSecondaryClick && (
            <Button
              variant="outline"
              className={size === 'prominent' ? 'text-base px-6 py-3' : ''}
              onClick={onSecondaryClick}
            >
              {secondaryButtonText}
            </Button>
          )}
        </div>

        {/* Social Proof */}
        {socialProof && (
          <p className="text-xs text-muted-foreground text-center pt-2 border-t">
            {socialProof}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Inline CTA Banner
 * Smaller CTA for placement within content
 */
export interface InlineCTABannerProps {
  message: string
  ctaText: string
  onClick: () => void
  variant?: 'info' | 'success' | 'warning'
  className?: string
}

export function InlineCTABanner({
  message,
  ctaText,
  onClick,
  variant = 'info',
  className,
}: InlineCTABannerProps) {
  const variantStyles = {
    info: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100',
    success: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100',
    warning: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100',
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 px-4 py-3 rounded-lg border',
        variantStyles[variant],
        className
      )}
    >
      <p className="text-sm font-medium">{message}</p>
      <Button size="sm" onClick={onClick}>
        {ctaText}
      </Button>
    </div>
  )
}

/**
 * Exit Intent CTA
 * Full-screen or modal CTA for when user is about to leave
 */
export interface ExitIntentCTAProps {
  title: string
  description: string
  primaryCTA: {
    text: string
    action: () => void
  }
  secondaryCTA?: {
    text: string
    action: () => void
  }
  onClose: () => void
  isOpen: boolean
}

export function ExitIntentCTA({
  title,
  description,
  primaryCTA,
  secondaryCTA,
  onClose,
  isOpen,
}: ExitIntentCTAProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl p-8 max-w-md mx-4">
        <h2 className="text-2xl font-bold text-foreground mb-4">{title}</h2>
        <p className="text-muted-foreground mb-6">{description}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button size="lg" className="flex-1" onClick={primaryCTA.action}>
            {primaryCTA.text}
          </Button>
          {secondaryCTA && (
            <Button variant="outline" size="lg" className="flex-1" onClick={secondaryCTA.action}>
              {secondaryCTA.text}
            </Button>
          )}
        </div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  )
}
